import React, { useState, useEffect } from 'react';
import { Activity, TrendingUp, TrendingDown, BarChart3 } from 'lucide-react';
import api from '../../utils/apiClient';

const MarketOverviewSection = ({ refreshTrigger }) => {
  const [vixData, setVixData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchVIXData();
  }, [refreshTrigger]); // Refresh when trigger changes

  const fetchVIXData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/api/stocks/vix');
      const d = response.data;
      if (!d.success) {
        setVixData(null);
        setError(d.message || d.error || 'Market overview unavailable');
        return;
      }
      setVixData(d);
    } catch (err) {
      console.error('Error fetching VIX data:', err);
      const body = err.response?.data;
      setVixData(null);
      setError(
        body?.message || body?.error || err.message || 'Failed to load market overview'
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="h-24 bg-gray-200 rounded"></div>
            <div className="h-24 bg-gray-200 rounded"></div>
            <div className="h-24 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !vixData) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <p className="text-red-600">Error loading market overview: {error || 'No data available'}</p>
      </div>
    );
  }

  const getVIXStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'low':
        return 'text-green-600 bg-green-50';
      case 'medium':
        return 'text-yellow-600 bg-yellow-50';
      case 'high':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment?.toLowerCase()) {
      case 'bullish':
        return 'text-green-600 bg-green-50';
      case 'bearish':
        return 'text-red-600 bg-red-50';
      case 'neutral':
        return 'text-yellow-600 bg-yellow-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getSpreadTypeColor = (spreadType) => {
    switch (spreadType?.toLowerCase()) {
      case 'contango':
        return 'text-green-600';
      case 'backwardation':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
        <Activity className="h-6 w-6 mr-2" />
        Market Overview
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* VIX Score Card */}
        <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700">VIX Score</h3>
            <Activity className="h-5 w-5 text-blue-600" />
          </div>
          <p className="text-3xl font-bold text-blue-900 mb-2">
            {vixData.vix ? vixData.vix.toFixed(2) : 'N/A'}
          </p>
          {vixData.vixSource && (
            <p className="text-xs text-gray-500 mb-1">
              Source:{' '}
              {vixData.vixSource === 'minute'
                ? 'Real-time (minute)'
                : vixData.vixSource === 'daily'
                  ? 'Daily'
                  : vixData.vixSource}
            </p>
          )}
          <div className="flex items-center justify-between">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getVIXStatusColor(vixData.vixStatus)}`}>
              {vixData.vixStatus || 'Unknown'}
            </span>
            {vixData.vixChange && (
              <span className={`text-sm font-medium ${
                vixData.vixChange >= 0 ? 'text-red-600' : 'text-green-600'
              }`}>
                {vixData.vixChange >= 0 ? '+' : ''}{vixData.vixChange.toFixed(2)}%
              </span>
            )}
          </div>
        </div>

        {/* Market Sentiment Card */}
        <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border border-purple-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700">Market Sentiment</h3>
            {vixData.marketSentiment?.toLowerCase() === 'bullish' ? (
              <TrendingUp className="h-5 w-5 text-green-600" />
            ) : vixData.marketSentiment?.toLowerCase() === 'bearish' ? (
              <TrendingDown className="h-5 w-5 text-red-600" />
            ) : (
              <BarChart3 className="h-5 w-5 text-yellow-600" />
            )}
          </div>
          <p className="text-3xl font-bold text-purple-900 mb-2 capitalize">
            {vixData.marketSentiment || 'Neutral'}
          </p>
          <div className="flex items-center justify-between">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSentimentColor(vixData.marketSentiment)}`}>
              {vixData.sentimentConfidence ? `${vixData.sentimentConfidence}% Confidence` : 'Confidence: N/A'}
            </span>
          </div>
          {vixData.sentimentReasoning && (
            <p className="text-xs text-gray-600 mt-2">{vixData.sentimentReasoning}</p>
          )}
        </div>

        {/* Dow Jones Card */}
        {vixData.dowJones && vixData.dowJones.success === false && (
          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
            <h3 className="text-sm font-medium text-gray-700 mb-1">Dow Jones</h3>
            <p className="text-sm text-amber-900">
              {vixData.dowJones.message || 'Dow Jones data unavailable.'}
            </p>
          </div>
        )}
        {vixData.dowJones && vixData.dowJones.success !== false && (
          <div className="p-4 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg border border-indigo-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-700">Dow Jones</h3>
              <BarChart3 className="h-5 w-5 text-indigo-600" />
            </div>
            <p className="text-3xl font-bold text-indigo-900 mb-2">
              {vixData.dowJones.price != null
                ? vixData.dowJones.price.toLocaleString('en-US', { maximumFractionDigits: 0 })
                : 'N/A'}
            </p>
            <div className="flex items-center justify-between">
              {vixData.dowJones.changePercent != null && vixData.dowJones.changePercent !== undefined && (
                <span className={`text-sm font-medium ${
                  vixData.dowJones.changePercent >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {vixData.dowJones.changePercent >= 0 ? '+' : ''}{vixData.dowJones.changePercent.toFixed(2)}%
                </span>
              )}
              {vixData.dowJones.change != null && vixData.dowJones.change !== undefined && (
                <span className={`text-xs font-medium ${
                  vixData.dowJones.change >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {vixData.dowJones.change >= 0 ? '+' : ''}{vixData.dowJones.change.toFixed(2)}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Source: {vixData.dowJones.source || 'yahoo_finance'}
              {vixData.dowJones.timestamp && ` (${new Date(vixData.dowJones.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })})`}
            </p>
          </div>
        )}

        {/* VIX Futures Card */}
        <div className="p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg border border-orange-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700">VIX Futures</h3>
            <BarChart3 className="h-5 w-5 text-orange-600" />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">VX1 (0-day):</span>
              <span className="text-sm font-semibold text-gray-900">
                {vixData.vx1 ? vixData.vx1.toFixed(2) : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-600">VX2 (30-day):</span>
              <span className="text-sm font-semibold text-gray-900">
                {vixData.vx2 ? vixData.vx2.toFixed(2) : 'N/A'}
              </span>
            </div>
            {vixData.vx2Estimated && (
              <p className="text-xs text-gray-500 mt-1">VX2 and spread are estimated from spot VIX (not live futures quotes).</p>
            )}
            <div className="pt-2 border-t border-orange-200">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-600">Futures Spread:</span>
                <span className={`text-sm font-bold ${getSpreadTypeColor(vixData.spreadType)}`}>
                  {vixData.futuresSpread ? `${vixData.futuresSpread >= 0 ? '+' : ''}${vixData.futuresSpread.toFixed(2)}%` : 'N/A'}
                </span>
              </div>
              {vixData.spreadType && (
                <span className={`text-xs font-medium mt-1 block ${getSpreadTypeColor(vixData.spreadType)}`}>
                  {vixData.spreadType}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketOverviewSection;

